import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { useLocale } from '../context/LocaleContext';
import { TrendingUp, TrendingDown, DollarSign, AlertCircle, Target, Sparkles, RefreshCw, PiggyBank, CreditCard } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const SmartFinancialDashboard = () => {
  const { toast } = useToast();
  const { t } = useLocale();
  const [dashboard, setDashboard] = useState(null);
  const [trends, setTrends] = useState(null);
  const [budget, setBudget] = useState(null);
  const [goals, setGoals] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBudgetDialog, setShowBudgetDialog] = useState(false);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [budgetForm, setBudgetForm] = useState({
    monthly_budget: 3000,
    category_budgets: {
      utilities: 500,
      groceries: 600,
      transportation: 300,
      entertainment: 200,
      healthcare: 400,
      other: 1000
    }
  });
  const [newGoal, setNewGoal] = useState({
    name: '',
    target_amount: '',
    current_amount: 0,
    target_date: '',
    category: 'savings'
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, trendsRes, budgetRes, goalsRes, suggestionsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/life/financial-dashboard`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/life/spending-trends`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/life/budget`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/life/financial-goals`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/life/recurring-bill-suggestions`, { credentials: 'include' })
      ]);

      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
      if (trendsRes.ok) setTrends(await trendsRes.json());
      if (budgetRes.ok) setBudget(await budgetRes.json());
      if (goalsRes.ok) {
        const goalsData = await goalsRes.json();
        setGoals(goalsData.goals || []);
      }
      if (suggestionsRes.ok) {
        const suggestionsData = await suggestionsRes.json();
        setSuggestions(suggestionsData.suggestions || []);
      }
    } catch (error) {
      console.error('Error loading financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/life/financial-goals`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ...newGoal,
            target_amount: parseFloat(newGoal.target_amount),
            current_amount: parseFloat(newGoal.current_amount || 0)
          })
        }
      );

      if (response.ok) {
        toast({ title: '✅ Goal Created!', description: `${newGoal.name} goal added` });
        setShowGoalDialog(false);
        setNewGoal({ name: '', target_amount: '', current_amount: 0, target_date: '', category: 'savings' });
        loadAllData();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create goal', variant: 'destructive' });
    }
  };

  // Initialize budget form when budget data loads
  useEffect(() => {
    if (budget?.budget) {
      setBudgetForm({
        monthly_budget: budget.budget.monthly_budget || 3000,
        category_budgets: budget.budget.category_budgets || {
          utilities: 500,
          groceries: 600,
          transportation: 300,
          entertainment: 200,
          healthcare: 400,
          other: 1000
        }
      });
    }
  }, [budget]);

  const handleUpdateBudget = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/life/budget`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(budgetForm)
        }
      );

      if (response.ok) {
        toast({ title: '✅ Budget Updated!', description: `Monthly budget set to $${budgetForm.monthly_budget}` });
        setShowBudgetDialog(false);
        loadAllData();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update budget', variant: 'destructive' });
    }
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6EDBC8'];
  const CATEGORY_COLORS = {
    utilities: '#3b82f6',
    groceries: '#10b981',
    transportation: '#f59e0b',
    entertainment: '#6EDBC8',
    healthcare: '#ef4444',
    other: '#6b7280'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">{t('life.loadingFinancialInsights')}</span>
      </div>
    );
  }

  // Prepare chart data with translations
  const categoryChartData = dashboard?.category_breakdown ? 
    Object.entries(dashboard.category_breakdown).map(([name, value]) => ({
      name: t(`life.categories.${name}`) || (name.charAt(0).toUpperCase() + name.slice(1)),
      value: value,
      color: CATEGORY_COLORS[name] || '#6b7280'
    })) : [];

  return (
    <div className="space-y-6">
      {/* Financial Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* This Month's Spending */}
        <Card className="border-2 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-gray-600">{t('life.thisMonthSpending')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-gray-900">
                ${dashboard?.current_month_total?.toFixed(2) || '0.00'}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              {dashboard?.change_percentage >= 0 ? (
                <TrendingUp className="h-4 w-4 text-red-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-green-500" />
              )}
              <span className={`text-sm font-medium ${
                dashboard?.change_percentage >= 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                {Math.abs(dashboard?.change_percentage || 0).toFixed(1)}% {t('life.vsLastMonth')}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Top Category */}
        <Card className="border-2 border-teal-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-gray-600">{t('life.topSpendingCategory')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Badge className="text-lg px-3 py-1" style={{ backgroundColor: CATEGORY_COLORS[dashboard?.top_category?.name] || '#6b7280' }}>
                {dashboard?.top_category?.name && dashboard.top_category.name !== 'none' 
                  ? (t(`life.categories.${dashboard.top_category.name.toLowerCase()}`) || dashboard.top_category.name) 
                  : t('life.noExpenses') || 'No expenses yet'}
              </Badge>
              <p className="text-2xl font-bold text-gray-900">
                ${dashboard?.top_category?.amount?.toFixed(2) || '0.00'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Bill Status */}
        <Card className="border-2 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-gray-600">{t('life.billStatus')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('life.paid')}:</span>
                <span className="font-semibold text-green-600">{dashboard?.bill_status?.paid || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('life.upcoming')}:</span>
                <span className="font-semibold text-yellow-600">{dashboard?.bill_status?.upcoming || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('life.overdue')}:</span>
                <span className="font-semibold text-red-600">{dashboard?.bill_status?.overdue || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Spending Trends */}
        {trends && trends.trends && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                {t('life.sixMonthTrend')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={trends.trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month_short" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                  <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-sm text-gray-600 mt-3">
                {t('common.average')}: <span className="font-semibold">${trends.average?.toFixed(2)}</span>/month
              </p>
            </CardContent>
          </Card>
        )}

      </div>

      {/* Budget Tracker */}
      {budget && (
        <Card className="border-2 border-indigo-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-indigo-600" />
              {t('life.budgetTracker')}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant={budget.status === 'over' ? 'destructive' : budget.status === 'warning' ? 'secondary' : 'default'}>
                {budget.percentage_used?.toFixed(1)}% {t('life.used')}
              </Badge>
              <Dialog open={showBudgetDialog} onOpenChange={setShowBudgetDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="text-indigo-600 border-indigo-300">
                    {t('life.setBudgetBtn')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{t('life.totalMonthlyBudget')}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUpdateBudget} className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">{t('life.totalMonthlyBudget')} ($)</Label>
                      <Input
                        type="number"
                        value={budgetForm.monthly_budget}
                        onChange={(e) => setBudgetForm({...budgetForm, monthly_budget: parseInt(e.target.value) || 0})}
                        placeholder={t('life.totalMonthlyBudget')}
                        min="0"
                        className="mt-1"
                      />
                    </div>
                    <div className="border-t pt-4">
                      <Label className="text-sm font-medium mb-3 block">{t('life.categoryBudgets')} ($)</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {Object.entries(budgetForm.category_budgets).map(([category, amount]) => (
                          <div key={category}>
                            <Label className="text-xs text-gray-600 capitalize">{t(`life.${category}`) || category}</Label>
                            <Input
                              type="number"
                              value={amount}
                              onChange={(e) => setBudgetForm({
                                ...budgetForm,
                                category_budgets: {
                                  ...budgetForm.category_budgets,
                                  [category]: parseInt(e.target.value) || 0
                                }
                              })}
                              min="0"
                              className="mt-1 h-9"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                      {t('life.saveBudget')}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Overall Budget */}
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">{t('life.overall')}: ${budget.total_spent?.toFixed(2)} / ${budget.total_budget}</span>
                  <span className="text-sm text-gray-600">${budget.total_remaining?.toFixed(2)} {t('life.left')}</span>
                </div>
                <Progress value={Math.min(100, budget.percentage_used)} className="h-3" />
              </div>

              {/* Category Budgets */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                {budget.category_status && Object.entries(budget.category_status).map(([category, status]) => (
                  <div key={category} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">{t(`life.categories.${category.toLowerCase()}`) || category}</span>
                      <Badge 
                        variant={status.status === 'over' ? 'destructive' : status.status === 'warning' ? 'secondary' : 'outline'}
                        className="text-xs"
                      >
                        {status.percentage?.toFixed(0)}%
                      </Badge>
                    </div>
                    <Progress value={Math.min(100, status.percentage)} className="h-2" />
                    <p className="text-xs text-gray-600 mt-1">
                      ${status.spent} / ${status.budget}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overdue Bills Alert */}
      {dashboard?.overdue_bills && dashboard.overdue_bills.length > 0 && (
        <Card className="border-2 border-red-300 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-900">
              <CreditCard className="h-5 w-5" />
              ⚠️ {t('life.overdueBills')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dashboard.overdue_bills.map((bill, idx) => (
                <div key={idx} className="p-3 bg-white rounded-lg border-2 border-red-300">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-900">{bill.name}</p>
                      <p className="text-sm text-red-600">{t('life.due')}: {(() => {
                        // Parse as local date to avoid timezone issues
                        const str = typeof bill.dueDate === 'string' ? bill.dueDate.split('T')[0] : bill.dueDate;
                        const [year, month, day] = str.split('-').map(Number);
                        return new Date(year, month - 1, day).toLocaleDateString();
                      })()}</p>
                    </div>
                    <span className="text-lg font-bold text-red-700">${bill.amount}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SmartFinancialDashboard;